import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Zap, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCreditStatus } from "@/hooks/useCreditStatus";
import { TIERS } from "@/lib/pricing";
import { openTierCheckout } from "@/lib/checkout";

interface CreditWallModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  toolName?: string;
  cost?: number;
}

export function CreditWallModal({ open, onOpenChange, toolName, cost }: CreditWallModalProps) {
  const navigate = useNavigate();
  const { data: status } = useCreditStatus();

  const remaining = status?.credits_remaining ?? 0;
  const tier = status?.tier ?? "free";

  const lite = TIERS.lite;
  const pro = TIERS.pro;

  const handleTier = (t: "lite" | "pro") => {
    onOpenChange(false);
    void openTierCheckout(t);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center mb-2">
            <Sparkles className="h-6 w-6 text-indigo-600" />
          </div>
          <DialogTitle className="text-center text-xl">
            Not enough credits
          </DialogTitle>
          <DialogDescription className="text-center">
            {toolName ? `${toolName} requires ${cost ?? 0} credits.` : "This tool requires credits to run."}{" "}
            You have <span className="font-semibold text-foreground">{remaining}</span> credits left on the{" "}
            <span className="capitalize font-semibold text-foreground">{tier}</span> plan.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <button
            type="button"
            onClick={() => handleTier("lite")}
            className="w-full text-left rounded-lg border border-indigo-200 bg-indigo-50/50 p-4 cursor-pointer transition-all hover:border-indigo-400 hover:bg-indigo-50 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <div className="flex items-start gap-3">
              <Zap className="h-5 w-5 text-indigo-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-sm">{lite.label} — {lite.priceDisplay}{lite.cadence}</p>
                <p className="text-xs text-muted-foreground">
                  {lite.creditsDisplay} · {lite.hiredContactsDisplay}
                </p>
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => handleTier("pro")}
            className="relative w-full text-left rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50 p-4 cursor-pointer transition-all hover:border-indigo-400 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <span className="absolute -top-2 right-3 text-[10px] uppercase tracking-wide bg-indigo-600 text-white px-1.5 py-0.5 rounded">
              Most popular
            </span>
            <div className="flex items-start gap-3">
              <Crown className="h-5 w-5 text-indigo-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-sm">{pro.label} — {pro.priceDisplay}{pro.cadence}</p>
                <p className="text-xs text-muted-foreground">
                  {pro.creditsDisplay} · {pro.hiredContactsDisplay}
                </p>
              </div>
            </div>
          </button>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              onOpenChange(false);
              navigate("/pricing");
            }}
          >
            View plans
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
